/**
 * How a soldier's job is read — the single source of truth for the rank a role
 * carries, the Hebrew label a stored record shows and what happens to a record
 * filed under a job the roster no longer keeps.
 *
 * The six jobs themselves live with the other fleet enums in
 * `src/types/fleet.ts`; what lives here is everything that reasons about them.
 * Nothing else in the application may map a role to a rank or to a label: the
 * roster order (`src/core/fleet-filters.ts`), the roster screens and the
 * one-shot migration in `src/db/fleet-store.ts` all read this module.
 *
 * A record is never trusted to hold one of the six. Earlier builds offered
 * מכונאי, מטעין, חובש and מוקדן, and a phone that has been running since then
 * still holds them — so every read goes through {@link normalizeSoldierRole},
 * which is what keeps a legacy record legible instead of blank while the
 * migration catches up with it.
 */
import {
  DEFAULT_SOLDIER_ROLE,
  SOLDIER_ROLES,
  SOLDIER_ROLE_LABELS,
  SOLDIER_ROLE_RANK,
} from '../types/fleet';
import type { Soldier, SoldierRole } from '../types/fleet';

const KNOWN_ROLES: ReadonlySet<string> = new Set(SOLDIER_ROLES);

/**
 * Jobs earlier builds offered, mapped onto the six the company keeps.
 *
 * `commander` was the company commander and is still one — that is the same
 * person doing the same job, so dropping them to נהג would be a demotion the
 * unit never asked for. Every other retired job was a driving one, so it falls
 * back to {@link DEFAULT_SOLDIER_ROLE} exactly as an unrecognised value does.
 */
export const LEGACY_SOLDIER_ROLES: Readonly<Record<string, SoldierRole>> = {
  commander: 'company_commander',
  mechanic: DEFAULT_SOLDIER_ROLE,
  loader: DEFAULT_SOLDIER_ROLE,
  medic: DEFAULT_SOLDIER_ROLE,
  dispatcher: DEFAULT_SOLDIER_ROLE,
};

/**
 * The revision of the roles list a device has been brought onto. Bump it when
 * the list itself changes and every phone already in the field has to be refiled
 * — see `reconcileSoldierRoles()` in `src/db/fleet-store.ts`, which runs once per
 * revision and then records it.
 */
export const SOLDIER_ROLE_REVISION = 1;

/** True when a stored record is filed under something other than the six. */
export const isLegacySoldierRole = (role: string | null | undefined): boolean =>
  !KNOWN_ROLES.has(String(role ?? '').trim());

/**
 * The job a record is read as: its own where the roster still keeps it, the
 * mapped one for a retired job, and נהג for anything else — a blank, a missing
 * field or a value nobody recognises.
 */
export function normalizeSoldierRole(role: string | null | undefined): SoldierRole {
  const raw = String(role ?? '').trim();
  if (KNOWN_ROLES.has(raw)) return raw as SoldierRole;
  return LEGACY_SOLDIER_ROLES[raw] ?? DEFAULT_SOLDIER_ROLE;
}

/** The Hebrew label for whatever a record carries — never `undefined`. */
export const soldierRoleLabel = (role: string | null | undefined): string =>
  SOLDIER_ROLE_LABELS[normalizeSoldierRole(role)];

/**
 * Where the job puts a soldier in the reading order — `1` for the מ"פ down to
 * `6` for the נהג. This is the primary key the roster is sorted by; see
 * `sortSoldiersForRoster()`.
 */
export const soldierRoleRank = (role: string | null | undefined): number =>
  SOLDIER_ROLE_RANK[normalizeSoldierRole(role)];

/** What separates the name from the job — one hyphen, one space either side. */
export const SOLDIER_NAME_ROLE_SEPARATOR = ' - ';

/**
 * How a soldier is named on the roster row: the name they were filed under and
 * the job they hold, in that order — "בן פרץ - מ"פ", "נתי כהן - נהג".
 *
 * The roster is read at a glance and the row shows no other field, so who is
 * the מ"פ and who is a נהג has to be legible from the name line itself. The job
 * is read through {@link soldierRoleLabel}, which means a record a phone has
 * been carrying since an earlier build reads as the נהג it is filed as rather
 * than as a blank.
 *
 * A record with no name left to show is named by its job alone — a dangling
 * hyphen is worse than the label standing on its own.
 */
export function soldierDisplayName(
  soldier: Pick<Soldier, 'fullName' | 'role'> | null | undefined,
): string {
  const name = String(soldier?.fullName ?? '').trim();
  const label = soldierRoleLabel(soldier?.role);
  return name ? `${name}${SOLDIER_NAME_ROLE_SEPARATOR}${label}` : label;
}
